import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FreelancerSearchFilterToolbar } from '@pigeons/shared-search-utility/freelancer';
import type {
  FreelancerSearchFilterToolbarLabels,
  ModalSlotRenderProps,
  ToolbarBadgeProps,
  ToolbarButtonProps,
} from '@pigeons/shared-search-utility/freelancer';
import FreelancerCard from '../components/freelancers/FreelancerCard';
import FilterModal from '../components/freelancers/FilterModal';
import SortModal from '../components/freelancers/SortModal';
import { Button } from '../components/common/Button';
import SearchBar from '../components/common/SearchBar';
import { useFreelancerSearchStore } from '../stores/freelancerSearchStore';
import { useSearchStateStore } from '../stores/searchStateStore';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ServiceCategory } from '../types/service';
import {
  FreelancerSearchResult,
  AlphabeticalSortOption,
  AllianceCountSortOption,
  SortOption,
} from '../types/search';
import FreelancerProfileModal from '../components/freelancers/FreelancerProfileModal';
import { City } from '../types/profile';
import Badge from '../components/Badge';
import { languageOptions } from '../utils/languagesUtils';
import { useProfileStore } from '../stores/profileStore';
import { twMerge } from 'tailwind-merge';

interface FreelancerSearchSubmitParams {
  keyword?: string;
  filter_languages: string[];
  filter_service_category_ids: number[];
  filter_city_ids: number[];
  sort_order: SortOption[];
  filter_rating: number;
  filter_proximity: number;
  origin_lat?: number | null;
  origin_lon?: number | null;
}

const DEFAULT_SORT_ORDER: SortOption[] = ['az', 'allianceDesc'];

const buildFreelancerSearchParams = (
  state: ReturnType<typeof useSearchStateStore.getState>['freelancer'],
  originLat?: number | null,
  originLon?: number | null
): FreelancerSearchSubmitParams => ({
  keyword: state.keyword.trim() || undefined,
  filter_languages: state.selectedLanguages,
  filter_service_category_ids: state.selectedServices.map(service => service.id),
  filter_city_ids: state.selectedCities.map(city => city.id),
  sort_order: [state.alphabeticalSort, state.allianceCountSort],
  filter_rating: state.rating,
  filter_proximity: state.proximity,
  origin_lat: originLat,
  origin_lon: originLon,
});

const isDefaultSortOrder = (sortOrder: SortOption[]) =>
  sortOrder.length === DEFAULT_SORT_ORDER.length &&
  sortOrder.every((sort, index) => sort === DEFAULT_SORT_ORDER[index]);

const hasSearchIntent = (params: FreelancerSearchSubmitParams) =>
  !!params.keyword ||
  params.filter_languages.length > 0 ||
  params.filter_service_category_ids.length > 0 ||
  params.filter_city_ids.length > 0 ||
  params.filter_rating > 0 ||
  params.filter_proximity > 0 ||
  !isDefaultSortOrder(params.sort_order);

const areSearchParamsEqual = (
  current: FreelancerSearchSubmitParams,
  previous: FreelancerSearchSubmitParams | null
) => {
  if (!previous) return false;

  return (
    current.keyword === previous.keyword &&
    current.filter_rating === previous.filter_rating &&
    current.filter_proximity === previous.filter_proximity &&
    current.origin_lat === previous.origin_lat &&
    current.origin_lon === previous.origin_lon &&
    current.filter_languages.join('|') === previous.filter_languages.join('|') &&
    current.filter_service_category_ids.join('|') ===
      previous.filter_service_category_ids.join('|') &&
    current.filter_city_ids.join('|') === previous.filter_city_ids.join('|') &&
    current.sort_order.join('|') === previous.sort_order.join('|')
  );
};

const FreelancerToolbarButton = ({
  className,
  variant = 'primary',
  children,
  onClick,
}: ToolbarButtonProps) => {
  const variantMap: Record<
    string,
    'primary' | 'alternate' | 'transparent-with-border' | 'simple-text'
  > = {
    primary: 'primary',
    alternate: 'alternate',
    'transparent-with-border': 'transparent-with-border',
    'simple-text': 'simple-text',
    light: 'alternate',
  };

  return (
    <Button
      className={twMerge(className)}
      variant={variantMap[variant] ?? 'primary'}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

const FreelancerToolbarBadge = ({ variant, color, size, endIcon, children }: ToolbarBadgeProps) => {
  const safeVariant = variant === 'solid' ? 'solid' : 'light';
  const safeColor =
    color === 'primary' ||
    color === 'success' ||
    color === 'error' ||
    color === 'warning' ||
    color === 'info' ||
    color === 'light'
      ? color
      : 'primary';
  const safeSize = size === 'sm' || size === 'md' ? size : 'md';

  return (
    <Badge variant={safeVariant} color={safeColor} size={safeSize} endIcon={endIcon}>
      {children}
    </Badge>
  );
};

const FreelancersPage = () => {
  const { t } = useTranslation(['freelancers', 'common']);

  const [hasSearched, setHasSearched] = useState(false);
  const [selectedFreelancer, setSelectedFreelancer] = useState<FreelancerSearchResult | null>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const {
    freelancer,
    setFreelancerKeyword,
    setFreelancerLanguages,
    setFreelancerServices,
    setFreelancerCities,
    setFreelancerAlphabeticalSort,
    setFreelancerAllianceCountSort,
    setFreelancerRating,
    setFreelancerProximity,
  } = useSearchStateStore();

  const { profile } = useProfileStore();

  const selectedLanguages = freelancer.selectedLanguages;
  const selectedServices = freelancer.selectedServices;
  const selectedCities = freelancer.selectedCities;
  const alphabeticalSort = freelancer.alphabeticalSort;
  const allianceCountSort = freelancer.allianceCountSort;
  const rating = freelancer.rating;
  const proximity = freelancer.proximity;

  const setSelectedLanguages = (languages: string[]) => setFreelancerLanguages(languages);
  const setSelectedServices = (services: ServiceCategory[]) => setFreelancerServices(services);
  const setSelectedCities = (cities: City[]) => setFreelancerCities(cities);
  const setAlphabeticalSort = (sort: AlphabeticalSortOption) => setFreelancerAlphabeticalSort(sort);
  const setAllianceCountSort = (sort: AllianceCountSortOption) =>
    setFreelancerAllianceCountSort(sort);
  const setRating = (rating: number) => setFreelancerRating(rating);
  const setProximity = (proximity: number) => setFreelancerProximity(proximity);

  const filterToolbarLabels = useMemo<FreelancerSearchFilterToolbarLabels>(
    () => ({
      filterBy: t('filterBy', { ns: 'freelancers' }),
      sortBy: t('sortBy', { ns: 'freelancers' }),
      badgeLanguagePrefix: t('badges.language', { ns: 'common' }),
      badgeServicePrefix: t('badges.service', { ns: 'common' }),
      badgeCityPrefix: t('badges.city', { ns: 'common' }),
      badgeRatingPrefix: t('badges.rating', { ns: 'common' }),
      badgeProximityPrefix: t('badges.proximity', { ns: 'common' }),
      badgeAlphabeticalPrefix: t('badges.alphabetical', { ns: 'common' }),
      badgeAllianceCountPrefix: t('badges.allianceCount', { ns: 'common' }),
      sortAlphabeticalLabel: (opt: AlphabeticalSortOption) =>
        t(`sorts.${opt}`, { ns: 'freelancers' }),
      sortAllianceLabel: (opt: AllianceCountSortOption) => t(`sorts.${opt}`, { ns: 'freelancers' }),
    }),
    [t]
  );

  const {
    results,
    spotlightedResults,
    error,
    spotlightedError,
    hasMore,
    suggestions,
    suggestionsLoading,
    fetch_suggestions,
    search_freelancers,
    fetch_spotlighted_freelancers,
    fetch_more_freelancers,
    spotlightedLoading,
    freelancerSearchingLoading,
  } = useFreelancerSearchStore();

  const loading = spotlightedLoading || freelancerSearchingLoading;

  const handleSearchSubmit = useCallback(async () => {
    const state = useSearchStateStore.getState().freelancer;
    const nextParams = buildFreelancerSearchParams(
      state,
      profile?.city?.latitude,
      profile?.city?.longitude
    );
    const previousSearch = useFreelancerSearchStore.getState().lastSearchParams;
    const previousParams: FreelancerSearchSubmitParams | null = previousSearch
      ? {
          keyword: previousSearch.keyword?.trim() || undefined,
          filter_languages: previousSearch.filter_languages || [],
          filter_service_category_ids: previousSearch.filter_service_category_ids || [],
          filter_city_ids: previousSearch.filter_city_ids || [],
          sort_order: previousSearch.sort_order || [],
          filter_rating: previousSearch.filter_rating || 0,
          filter_proximity: previousSearch.filter_proximity || 0,
          origin_lat: previousSearch.origin_lat,
          origin_lon: previousSearch.origin_lon,
        }
      : null;

    if (!hasSearchIntent(nextParams)) {
      setHasSearched(false);
      await fetch_spotlighted_freelancers(profile?.city?.latitude, profile?.city?.longitude);
      return;
    }

    if (hasSearched && !error && areSearchParamsEqual(nextParams, previousParams)) {
      return;
    }

    setHasSearched(true);

    await search_freelancers(nextParams);
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    fetch_spotlighted_freelancers,
    hasSearched,
    error,
    search_freelancers,
    profile?.city?.latitude,
    profile?.city?.longitude,
  ]);

  const prevCoordsRef = useRef<{ lat?: number; lon?: number }>({});

  useEffect(() => {
    const currentLat = profile?.city?.latitude;
    const currentLon = profile?.city?.longitude;

    const coordsChanged =
      currentLat !== prevCoordsRef.current.lat || currentLon !== prevCoordsRef.current.lon;

    if (coordsChanged && currentLat && currentLon) {
      if (hasSearched) {
        handleSearchSubmit();
      } else {
        fetch_spotlighted_freelancers(currentLat, currentLon);
      }
      prevCoordsRef.current = { lat: currentLat, lon: currentLon };
    } else if (spotlightedResults.length === 0 && !hasSearched) {
      fetch_spotlighted_freelancers(currentLat, currentLon);
    }
  }, [
    hasSearched,
    spotlightedResults.length,
    fetch_spotlighted_freelancers,
    handleSearchSubmit,
    profile?.city?.latitude,
    profile?.city?.longitude,
  ]);

  useEffect(() => {
    const hasFilters =
      freelancer.keyword.trim() ||
      freelancer.selectedLanguages.length > 0 ||
      freelancer.selectedServices.length > 0 ||
      freelancer.selectedCities.length > 0 ||
      freelancer.rating > 0 ||
      freelancer.proximity > 0 ||
      freelancer.alphabeticalSort !== 'az' ||
      freelancer.allianceCountSort !== 'allianceDesc';

    if (hasFilters && !hasSearched) {
      handleSearchSubmit();
    }
    // Intentionally run only on mount to apply URL/initial filters once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const freelancers = hasSearched ? results : spotlightedResults;
  const searchError = hasSearched ? error : spotlightedError;
  useEffect(() => {
    const hasFilters =
      freelancer.selectedLanguages.length > 0 ||
      freelancer.selectedServices.length > 0 ||
      freelancer.selectedCities.length > 0 ||
      freelancer.rating > 0 ||
      freelancer.proximity > 0 ||
      freelancer.alphabeticalSort !== 'az' ||
      freelancer.allianceCountSort !== 'allianceDesc';

    if (freelancer.keyword.trim() === '' && !hasFilters) {
      setHasSearched(false);
      const currentLat = profile?.city?.latitude;
      const currentLon = profile?.city?.longitude;
      fetch_spotlighted_freelancers(currentLat, currentLon);
    }
  }, [
    freelancer.keyword,
    freelancer.selectedLanguages,
    freelancer.selectedServices,
    freelancer.selectedCities,
    freelancer.rating,
    freelancer.proximity,
    freelancer.alphabeticalSort,
    freelancer.allianceCountSort,
    profile?.city?.latitude,
    profile?.city?.longitude,
    fetch_spotlighted_freelancers,
  ]);

  return (
    <div className="flex flex-col md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
      <div className="flex w-full flex-col pt-4 md:w-2/3 md:pr-8">
        <h1 className="text-5xl font-bold text-secondary">{t('title', { ns: 'freelancers' })}</h1>
      </div>
      <div className="my-2 md:my-4">
        <div className="mb-4 md:mb-6">
          <SearchBar
            value={freelancer.keyword}
            onChange={value => setFreelancerKeyword(value)}
            suggestions={suggestions}
            onSelect={suggestion => {
              setFreelancerKeyword(suggestion);
              handleSearchSubmit();
            }}
            onSubmit={handleSearchSubmit}
            onFetchSuggestions={fetch_suggestions}
            loading={suggestionsLoading}
            placeholder={t('navigation.searchPlaceholder', { ns: 'common' })}
            className="w-full max-w-2xl"
          />
        </div>
        <FreelancerSearchFilterToolbar
          onApplySearch={handleSearchSubmit}
          labels={filterToolbarLabels}
          getLanguageDisplayLabel={(code: string) =>
            languageOptions.find(opt => opt.code === code)?.label || code
          }
          selectedLanguages={selectedLanguages}
          selectedServices={selectedServices}
          selectedCities={selectedCities}
          rating={rating}
          proximity={proximity}
          alphabeticalSort={alphabeticalSort}
          allianceCountSort={allianceCountSort}
          onRemoveLanguage={(lang: string) => {
            setSelectedLanguages(
              useSearchStateStore.getState().freelancer.selectedLanguages.filter(l => l !== lang)
            );
            void handleSearchSubmit();
          }}
          onRemoveService={(id: number) => {
            setSelectedServices(
              useSearchStateStore.getState().freelancer.selectedServices.filter(s => s.id !== id)
            );
            void handleSearchSubmit();
          }}
          onRemoveCity={(id: number) => {
            setSelectedCities(
              useSearchStateStore.getState().freelancer.selectedCities.filter(c => c.id !== id)
            );
            void handleSearchSubmit();
          }}
          onClearRating={() => {
            setRating(0);
            void handleSearchSubmit();
          }}
          onClearProximity={() => {
            setProximity(0);
            void handleSearchSubmit();
          }}
          onResetAlphabeticalSort={() => {
            setAlphabeticalSort('az');
            void handleSearchSubmit();
          }}
          onResetAllianceCountSort={() => {
            setAllianceCountSort('allianceDesc');
            void handleSearchSubmit();
          }}
          components={{ Button: FreelancerToolbarButton, Badge: FreelancerToolbarBadge }}
          renderFilterModal={({
            className,
            isBlocking,
            onClose,
            onSubmit,
          }: ModalSlotRenderProps) => (
            <FilterModal
              className={className}
              isBlocking={isBlocking}
              onClose={onClose}
              onSubmit={onSubmit}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              selectedCities={selectedCities}
              setSelectedCities={setSelectedCities}
              rating={rating}
              setRating={setRating}
              proximity={proximity}
              setProximity={setProximity}
            />
          )}
          renderSortModal={({ className, isBlocking, onClose, onSubmit }: ModalSlotRenderProps) => (
            <SortModal
              className={className}
              isBlocking={isBlocking}
              onClose={onClose}
              onSubmit={onSubmit}
              alphabeticalSort={alphabeticalSort}
              allianceCountSort={allianceCountSort}
              setAlphabeticalSort={setAlphabeticalSort}
              setAllianceCountSort={setAllianceCountSort}
            />
          )}
        />

        <div className="mt-4 pb-4" ref={searchResultsRef}>
          {freelancers.map(freelancer => (
            <div
              className="mb-6 rounded-xl border-2 border-gray-300 bg-white shadow-xl md:p-4"
              key={freelancer.id}
            >
              <FreelancerCard
                key={freelancer.id}
                freelancer={freelancer}
                complete={true}
                onClick={() => setSelectedFreelancer(freelancer)}
              />
            </div>
          ))}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {searchError && !loading && (
            <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-12">
              <p className="mb-2 font-semibold text-red-700">
                {t('searchErrorMessage', { ns: 'freelancers' })}
              </p>
              <button
                className="mt-2 rounded bg-red-600 px-4 py-2 text-white transition hover:bg-red-700"
                onClick={() => handleSearchSubmit()}
              >
                {t('retry', { ns: 'freelancers' })}
              </button>
              <p className="mt-2 text-xs text-red-500">
                {t('contactSupport', { ns: 'freelancers' })}
              </p>
            </div>
          )}

          {freelancers.length === 0 && !loading && !searchError && (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
              <p className="mb-2 text-lg font-semibold text-gray-500">
                {t('noResults', { ns: 'freelancers' })}
              </p>
              <p className="text-sm text-gray-400">{t('refineSearch', { ns: 'freelancers' })}</p>
            </div>
          )}

          {hasMore && !loading && (
            <div>
              <Button
                className="my-4 w-full md:w-auto md:justify-self-end md:px-8"
                onClick={() => fetch_more_freelancers()}
              >
                {t('loadMore', { ns: 'freelancers' })}
              </Button>
            </div>
          )}
        </div>
      </div>

      {selectedFreelancer && (
        <FreelancerProfileModal
          freelancer={selectedFreelancer}
          onClose={() => setSelectedFreelancer(null)}
        />
      )}
    </div>
  );
};

export default FreelancersPage;
